import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import { unifiedStorage } from "@/lib/unified-storage";
import {
  Mecanica,
  ParticipanteMecanica,
  AgencyMetrics,
  MecanicaInscritos,
  ParticipacaoPorHora,
  FaixaIdade,
  ConfiguracaoSolucao,
  RelatorioParticipante,
  UsuarioAcesso,
} from "@/lib/fakeApi";
import type { CustomFieldConfig } from "@/components/queue/CustomFieldsBuilder";

export enum OptinType {
  TEXT = "text",
  LINK = "link",
  DOCUMENT = "document",
}

export interface OptinConfig {
  key: string;
  label: string;
  type: OptinType;
  required: boolean;
  removable: boolean;
  order: number;
  content?: string;
  url?: string;
  documentUrl?: string;
  documentKey?: string;
  documentName?: string;
}

// Backend types
export interface Queue {
  _id: string;
  eventId: string;
  name: string;
  description?: string;
  type: "entry" | "gift" | "activity" | "food" | "custom";
  status: "active" | "paused" | "completed" | "inactive" | "cancelled";
  maxSize?: number;
  currentSize?: number; // Deprecated - use metrics.currentSize
  metrics?: {
    totalEntries: number;
    currentSize: number;
    averageWaitTime: number;
    totalServed: number;
    currentServingPosition: number;
    abandonmentRate: number;
  };
  settings?: {
    maxSize?: number;
    autoAdvance: boolean;
    requiresCheckIn: boolean;
    estimatedServiceTime: number;
    serviceStartTime?: string | Date;
    simultaneousAttendance?: number;
    recalculateOnLeave?: boolean;
    notifications?: {
      enableWhatsApp: boolean;
      enableSMS: boolean;
      advanceNotice: number;
    };
  };
  simultaneousAttendance?: number;
  currentlyServing?: number;
  servingEntries?: Array<{
    userId: string;
    position: number;
    participantName?: string;
  }>;
  notifyAtPosition?: number;
  startTime?: string;
  endTime?: string;
  createdAt: string;
  updatedAt: string;
}

export interface QueueEntry {
  _id?: string;
  id?: string;
  queueId: string;
  userId: string;
  userName?: string;
  userEmail?: string;
  userPhone?: string;
  userDocument?: string;
  participantName?: string;
  participantPhone?: string;
  participantEmail?: string;
  position: number;
  status: "waiting" | "serving" | "completed" | "abandoned" | "called" | "absent" | "CALLED" | "SERVING";
  joinedAt: string;
  servedAt?: string;
  completedAt?: string;
  estimatedWaitTime?: number;
  estimatedServiceTime?: Date | string;
  formattedTime?: string;
  isScheduled?: boolean;
  qrCode?: string;
  messageCount?: number;
  peopleAhead?: number;
  queueName?: string;
  eventId?: string;
  customFields?: Record<string, unknown>;
}

interface DashboardData {
  statusBreakdown: {
    waiting: number;
    completed: number;
    abandoned: number;
    absent: number;
    cancelled: number;
  };
  hourlyParticipation: Array<{
    hour: string;
    count: number;
  }>;
  queueDistribution: Array<{
    queueId: string;
    queueName: string;
    count: number;
  }>;
  totalEntries: number;
}

interface CreateQueueDto {
  name: string;
  description?: string;
  type: "entry" | "gift" | "activity" | "food" | "custom";
  status?: "inactive" | "active" | "paused" | "completed";
  settings: {
    maxSize?: number;
    autoAdvance: boolean;
    requiresCheckIn: boolean;
    estimatedServiceTime: number;
    simultaneousAttendance?: number;
    serviceStartTime?: string | Date;
    notifications: {
      enableWhatsApp: boolean;
      enableSMS: boolean;
      advanceNotice: number;
      templates: {
        joined?: string;
        position?: string;
        yourTurn?: string;
        completed?: string;
      };
    };
  };
}

interface UpdateQueueDto extends Partial<CreateQueueDto> {
  status?: "active" | "paused" | "completed";
}

interface AddParticipantDto {
  participantName: string;
  participantPhone: string;
  participantEmail?: string;
  metadata?: {
    joinMethod: "web" | "self" | "staff" | "import";
    device?: string;
    location?: string;
    notes?: string;
  };
}

interface PaginationParams {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}

interface ParticipantList {
  entries: QueueEntry[];
  total: number;
  page: number;
  limit: number;
}

class QueueApiService {
  private api: AxiosInstance;
  private baseUrl: string;
  private maxRetries = 3;
  private retryDelay = 1000; // 1 second

  constructor() {
    this.baseUrl = import.meta.env.VITE_QUEUE_API || "http://localhost:3001";

    this.api = axios.create({
      baseURL: `${this.baseUrl}/api/v1`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });

    // Request interceptor for auth
    this.api.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        const eventId = this.getEventId();
        const userId = this.getUserId();
        const userProfile = this.getUserProfile();

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add event context headers
        if (eventId) {
          config.headers["X-Event-Id"] = eventId;
        }

        // Log only for API calls (not health checks)
        if (
          config.url?.includes("/queues") ||
          config.url?.includes("/entries")
        ) {
          console.log("[QueueAPI] Request:", {
            url: config.url,
            hasToken: !!token,
            eventId: eventId || "missing",
          });
        }

        // Only add user headers if we have authentication
        if (token) {
          if (userId) config.headers["X-User-Id"] = userId;
          if (userProfile) config.headers["X-Profile"] = userProfile;
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling and retry logic
    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & {
          _retry?: number;
        };

        // Initialize retry count
        if (!originalRequest._retry) {
          originalRequest._retry = 0;
        }

        // Handle network errors with retry logic
        if (!error.response && originalRequest._retry < this.maxRetries) {
          originalRequest._retry++;
          console.log(
            `Network error, retrying... (${originalRequest._retry}/${this.maxRetries})`
          );

          // Wait before retrying with exponential backoff
          await new Promise((resolve) =>
            setTimeout(
              resolve,
              this.retryDelay * Math.pow(2, (originalRequest._retry || 1) - 1)
            )
          );

          return this.api(originalRequest);
        }

        // Handle specific HTTP status codes
        if (error.response?.status === 401) {
          // Try to refresh token
          const refreshed = await this.refreshToken();
          if (!refreshed) {
            // Don't clear auth data immediately - let the user handle it
            console.warn(
              "[QueueAPI] Authentication failed (401), token may be expired"
            );
            // Don't clear storage automatically - this might be a temporary issue
            // unifiedStorage.clearAll(); // REMOVED - don't clear automatically
            // Don't redirect - stay in app
          }
        } else if (error.response?.status === 403) {
          // Handle forbidden access - user lacks permissions
          console.error("Access forbidden:", error.response.data);
        } else if (
          error.response &&
          error.response.status >= 500 &&
          (!originalRequest._retry || originalRequest._retry < this.maxRetries)
        ) {
          // Retry on server errors
          originalRequest._retry = (originalRequest._retry || 0) + 1;
          console.log(
            `Server error, retrying... (${originalRequest._retry}/${this.maxRetries})`
          );

          await new Promise((resolve) =>
            setTimeout(
              resolve,
              this.retryDelay * Math.pow(2, (originalRequest._retry || 1) - 1)
            )
          );

          return this.api(originalRequest);
        } else if (error.response && error.response.status >= 500) {
          // Log server errors for debugging after max retries
          console.error(
            "Server error after max retries:",
            error.response?.data
          );
        }

        return Promise.reject(error);
      }
    );
  }

  // Auth helpers
  private getToken(): string | null {
    const tokens = unifiedStorage.getTokens();
    if (tokens?.accessToken) {
      return tokens.accessToken;
    }
    console.log("[QueueAPI] No token found in UnifiedStorage");
    return null;
  }

  private getEventId(): string | null {
    // Get from URL params or UnifiedStorage
    const params = new URLSearchParams(window.location.search);
    const urlEventId = params.get("eventId") || params.get("eventoId");
    if (urlEventId) {
      console.log("[QueueAPI] Using event ID from URL:", urlEventId);
      // Also store it for later use
      unifiedStorage.setEventId(urlEventId);
      return urlEventId;
    }

    const storedEventId = unifiedStorage.getEventId();
    if (storedEventId) {
      console.log(
        "[QueueAPI] Using event ID from UnifiedStorage:",
        storedEventId
      );
      return storedEventId;
    }

    // Try to extract from pathname if it contains event ID pattern
    const pathMatch = window.location.pathname.match(
      /evento[s]?\/([a-f0-9]{24})/i
    );
    if (pathMatch && pathMatch[1]) {
      console.log("[QueueAPI] Using event ID from path:", pathMatch[1]);
      unifiedStorage.setEventId(pathMatch[1]);
      return pathMatch[1];
    }

    console.log("[QueueAPI] No event ID found!");
    return null;
  }

  private getUserId(): string | null {
    const user = unifiedStorage.getUser();
    return user?.id || null;
  }

  private getUserProfile(): string | null {
    const user = unifiedStorage.getUser();
    return user?.profile || "admin";
  }

  private async refreshToken(): Promise<boolean> {
    try {
      // Implement token refresh logic if needed
      return false;
    } catch {
      return false;
    }
  }

  // Mapping functions
  private mapQueueToMecanica(
    queue: Queue
  ): Mecanica & {
    serviceStartTime?: string | Date;
    estimatedServiceTime?: number;
    simultaneousAttendance?: number;
  } {
    const statusMap: Record<string, "aberta" | "fechada"> = {
      active: "aberta",
      inactive: "fechada",
      paused: "fechada",
      completed: "fechada",
      cancelled: "fechada",
    };

    // Use metrics.currentSize if available, fallback to currentSize or 0
    const participantCount =
      queue.metrics?.currentSize ?? queue.currentSize ?? 0;

    return {
      id: queue._id,
      nome: queue.name,
      quantidadeParticipacao: participantCount,
      status: statusMap[queue.status] || "fechada",
      termosParticipacao: queue.description || "",
      urlPublica: `${window.location.origin}/usuario?queueId=${queue._id}`,
      realStatus: queue.status, // Adiciona o status real do backend
      eventId: queue.eventId, // Preserva o eventId para carregar custom fields
      // Include scheduling fields from settings
      maxParticipants: queue.settings?.maxSize ?? undefined,
      serviceStartTime: queue.settings?.serviceStartTime,
      estimatedServiceTime: queue.settings?.estimatedServiceTime,
      simultaneousAttendance: queue.settings?.simultaneousAttendance,
    };
  }

  private mapMecanicaToQueue(
    mecanica: Partial<
      Mecanica & {
        maxParticipants?: number;
        estimatedServiceTime?: number;
        simultaneousAttendance?: number;
        serviceStartTime?: string | Date;
      }
    >
  ): CreateQueueDto | UpdateQueueDto {
    const statusMap: Record<
      string,
      "active" | "inactive" | "paused" | "completed"
    > = {
      aberta: "active",
      fechada: "inactive",
    };

    return {
      name: mecanica.nome || "",
      description: mecanica.termosParticipacao || undefined,
      type: "entry", // Default type for mechanics
      status: mecanica.status ? statusMap[mecanica.status] : "inactive",
      settings: {
        maxSize: mecanica.maxParticipants || undefined,
        autoAdvance: false,
        requiresCheckIn: false,
        estimatedServiceTime: mecanica.estimatedServiceTime || 5, // Use provided value or default to 5 minutes
        simultaneousAttendance: mecanica.simultaneousAttendance || 1, // Include simultaneous attendance
        serviceStartTime: mecanica.serviceStartTime || undefined, // Include scheduled start time
        notifications: {
          enableWhatsApp: true,
          enableSMS: false,
          advanceNotice: 3, // 3 positions ahead
          templates: {
            joined: `Olá! Você foi adicionado à fila. Posição: {position}`,
            position: `Sua posição atual: {position}. Tempo estimado: {waitTime} minutos`,
            yourTurn: `É sua vez! Dirija-se ao local de atendimento`,
            completed: `Atendimento concluído. Obrigado!`,
          },
        },
      },
    };
  }

  private mapEntryToParticipant(entry: QueueEntry): ParticipanteMecanica {
    const statusMap: Record<string, ParticipanteMecanica["status"]> = {
      waiting: "na_fila",
      called: "na_fila",
      serving: "na_fila",
      completed: "concluido",
      abandoned: "desistente",
      absent: "ausente",
      cancelled: "cancelado",
    };

    return {
      id: entry._id || entry.id || "",
      mecanicaId: entry.queueId,
      nome: entry.participantName || entry.userName || "Sem nome",
      telefone: entry.participantPhone || entry.userPhone || "",
      status: statusMap[entry.status] || "na_fila",
      estimativa:
        entry.estimatedWaitTime !== undefined &&
        entry.estimatedWaitTime !== null
          ? `${entry.estimatedWaitTime} min`
          : "",
      cadastro: entry.joinedAt,
      posicao: entry.position,
      numeroMensagem: entry.messageCount || 0,
      customFields: (entry.customFields || (entry as any).metadata?.customFields) as Record<string, any> | undefined,
    };
  }

  private mapParticipantToEntry(
    participant: Partial<ParticipanteMecanica>
  ): AddParticipantDto {
    return {
      participantName: participant.nome || "",
      participantPhone: participant.telefone || "",
      participantEmail: "", // Email if available
      metadata: {
        joinMethod: "web" as const,
        notes: "", // Any notes if available
      },
    };
  }

  // Queue CRUD operations
  async listQueues(): Promise<
    (Mecanica & {
      serviceStartTime?: string | Date;
      estimatedServiceTime?: number;
      simultaneousAttendance?: number;
    })[]
  > {
    try {
      const eventId = this.getEventId();
      if (!eventId) {
        console.warn(
          "[QueueAPI] No event ID available, queues will be filtered by backend context"
        );
      }
      // EventId is sent via X-Event-Id header in the interceptor
      const response = await this.api.get<Queue[]>("/queues");
      return response.data.map((queue) => this.mapQueueToMecanica(queue));
    } catch (error) {
      console.error("Error listing queues:", error);
      throw error;
    }
  }

  async getQueue(
    id: string
  ): Promise<
    Mecanica & {
      serviceStartTime?: string | Date;
      estimatedServiceTime?: number;
      simultaneousAttendance?: number;
    }
  > {
    try {
      const response = await this.api.get<Queue>(`/queues/${id}`);
      return this.mapQueueToMecanica(response.data);
    } catch (error) {
      console.error("Error getting queue:", error);
      throw error;
    }
  }

  async createQueue(
    data: Partial<
      Mecanica & {
        maxParticipants?: number;
        estimatedServiceTime?: number;
        simultaneousAttendance?: number;
        serviceStartTime?: string | Date;
      }
    >
  ): Promise<
    Mecanica & {
      serviceStartTime?: string | Date;
      estimatedServiceTime?: number;
      simultaneousAttendance?: number;
    }
  > {
    try {
      const eventId = this.getEventId();
      if (!eventId) {
        throw new Error("Event ID is required to create a queue");
      }

      // Validate required fields
      if (!data.nome || data.nome.trim() === "") {
        throw new Error("Queue name is required");
      }

      // Don't send eventId in body - it's added by the backend from context
      const queueData = this.mapMecanicaToQueue(data);
      const response = await this.api.post<Queue>("/queues", queueData);
      return this.mapQueueToMecanica(response.data);
    } catch (error) {
      console.error("Error creating queue:", error);
      throw error;
    }
  }

  async updateQueue(
    id: string,
    data: Partial<
      Mecanica & {
        maxParticipants?: number;
        estimatedServiceTime?: number;
        simultaneousAttendance?: number;
        serviceStartTime?: string | Date;
      }
    >
  ): Promise<
    Mecanica & {
      serviceStartTime?: string | Date;
      estimatedServiceTime?: number;
      simultaneousAttendance?: number;
    }
  > {
    try {
      const queueData = this.mapMecanicaToQueue(data);
      const response = await this.api.put<Queue>(`/queues/${id}`, queueData);
      return this.mapQueueToMecanica(response.data);
    } catch (error) {
      console.error("Error updating queue:", error);
      throw error;
    }
  }

  async deleteQueue(id: string): Promise<void> {
    try {
      await this.api.delete(`/queues/${id}`);
    } catch (error) {
      console.error("Error deleting queue:", error);
      throw error;
    }
  }

  async updateQueueStatus(
    id: string,
    status: "active" | "inactive" | "paused" | "completed"
  ): Promise<void> {
    try {
      await this.api.put(`/queues/${id}/status`, { status });
    } catch (error) {
      console.error("Error updating queue status:", error);
      throw error;
    }
  }

  async cancelQueue(id: string, reason: string): Promise<void> {
    try {
      await this.api.put(`/queues/${id}/cancel`, { reason });
    } catch (error) {
      console.error("Error cancelling queue:", error);
      throw error;
    }
  }

  async getQueueCapacity(
    id: string
  ): Promise<{
    current: number;
    max: number | null;
    available: number | null;
    isFull: boolean;
  }> {
    try {
      const response = await this.api.get<{
        current: number;
        max: number | null;
        available: number | null;
        isFull: boolean;
      }>(`/queues/${id}/capacity`);
      return response.data;
    } catch (error) {
      console.error("Error getting queue capacity:", error);
      throw error;
    }
  }

  async resetQueue(queueId: string): Promise<{ message: string; data: { modifiedCount: number } }> {
    try {
      const response = await this.api.put<{ message: string; data: { modifiedCount: number } }>(
        `/queues/${queueId}/reset`
      );
      return response.data;
    } catch (error) {
      console.error("Error resetting queue:", error);
      throw error;
    }
  }

  async getActiveEntriesCount(queueId: string): Promise<{ count: number }> {
    try {
      const response = await this.api.get<{ count: number }>(
        `/queues/${queueId}/active-entries-count`
      );
      return response.data;
    } catch (error) {
      console.error("Error getting active entries count:", error);
      throw error;
    }
  }

  // Participant operations
  async listParticipants(
    queueId: string,
    params?: PaginationParams
  ): Promise<ParticipanteMecanica[]> {
    try {
      // Set default values for pagination to get all results
      const queryParams = {
        queueId,
        page: params?.page || 1,
        limit: params?.limit || 1000, // Get many results to avoid pagination issues
        status: params?.status || undefined,
        search: params?.search || undefined,
      };

      const response = await this.api.get<ParticipantList>("/entries", {
        params: queryParams,
      });
      return response.data.entries.map((entry) =>
        this.mapEntryToParticipant(entry)
      );
    } catch (error) {
      console.error("Error listing participants:", error);
      throw error;
    }
  }

  async listParticipantsWithPagination(
    queueId: string,
    params?: PaginationParams
  ): Promise<{
    entries: ParticipanteMecanica[];
    total: number;
    page: number;
    limit: number;
  }> {
    try {
      const queryParams = {
        queueId,
        page: params?.page || 1,
        limit: params?.limit || 20,
        status: params?.status || undefined,
        search: params?.search || undefined,
      };

      const response = await this.api.get<ParticipantList>("/entries", {
        params: queryParams,
      });

      return {
        entries: response.data.entries.map((entry) =>
          this.mapEntryToParticipant(entry)
        ),
        total: response.data.total,
        page: response.data.page,
        limit: response.data.limit,
      };
    } catch (error) {
      console.error("Error listing participants with pagination:", error);
      throw error;
    }
  }

  async addParticipant(
    queueId: string,
    data: Partial<ParticipanteMecanica>
  ): Promise<ParticipanteMecanica> {
    try {
      // Validate required fields
      if (!queueId) {
        throw new Error("Queue ID is required to add participant");
      }

      if (!data.nome || data.nome.trim() === "") {
        throw new Error("Participant name is required");
      }

      const participantData = this.mapParticipantToEntry(data);
      const response = await this.api.post<QueueEntry>(
        `/queues/${queueId}/entries/join`,
        {
          ...participantData,
        }
      );
      return this.mapEntryToParticipant(response.data);
    } catch (error) {
      console.error("Error adding participant:", error);
      throw error;
    }
  }

  async updateParticipantStatus(
    id: string,
    status: ParticipanteMecanica["status"],
    currentStatus?: ParticipanteMecanica["status"]
  ): Promise<ParticipanteMecanica> {
    try {
      const statusMap: Record<ParticipanteMecanica["status"], string> = {
        na_fila: "waiting",
        concluido: "completed",
        desistente: "abandoned",
        ausente: "abandoned",
        cancelado: "cancelled",
      };

      const newBackendStatus = statusMap[status];
      const currentBackendStatus = currentStatus
        ? statusMap[currentStatus]
        : null;

      // Build the request payload
      const payload: any = {
        status: newBackendStatus,
      };

      // Include reason when transitioning to 'waiting' from abandoned, absent, or cancelled
      // Check both frontend and backend status values
      if (newBackendStatus === "waiting") {
        if (
          currentStatus === "desistente" ||
          currentStatus === "ausente" ||
          currentBackendStatus === "abandoned"
        ) {
          payload.reason = "Participante retornou à fila";
        } else if (
          currentStatus === "cancelado" ||
          currentBackendStatus === "cancelled"
        ) {
          payload.reason =
            "Cancelamento revertido - participante retornou à fila";
        } else if (
          currentStatus === "concluido" ||
          currentBackendStatus === "completed"
        ) {
          // This transition is blocked by backend, but include reason just in case
          payload.reason =
            "Atendimento revertido - participante retornou à fila";
        } else if (currentStatus && currentStatus !== "na_fila") {
          // Fallback for any other status
          payload.reason = "Participante retornou à fila";
        }
      }

      // Note: PATCH is correct here, EntriesController uses PATCH for status updates
      const response = await this.api.patch<QueueEntry>(
        `/entries/${id}/status`,
        payload
      );
      return this.mapEntryToParticipant(response.data);
    } catch (error) {
      console.error("Error updating participant status:", error);
      throw error;
    }
  }

  async removeParticipant(id: string): Promise<void> {
    try {
      await this.api.delete(`/entries/${id}`);
    } catch (error) {
      console.error("Error removing participant:", error);
      throw error;
    }
  }

  async incrementParticipantMessage(id: string): Promise<number> {
    try {
      const response = await this.api.post<{ messageCount: number }>(
        `/entries/${id}/message`
      );
      return response.data.messageCount;
    } catch (error) {
      console.error("Error incrementing message count:", error);
      throw error;
    }
  }

  // Public queue information (no auth required)
  async getPublicQueueInfo(queueId: string): Promise<Queue> {
    try {
      const response = await this.api.get<Queue>(`/queues/public/${queueId}`);
      return response.data;
    } catch (error) {
      console.error("Error getting public queue info:", error);
      throw error;
    }
  }

  // Public participant methods for queue joining
  async joinQueue(
    queueId: string,
    participantData: {
      name: string;
      phone?: string;
      email?: string;
      notificationPreferences?: any;
      customFields?: Record<string, any>;
      optins?: Record<string, boolean>;
    }
  ): Promise<QueueEntry> {
    try {
      const requestBody = {
        participantName: participantData.name,
        participantPhone: participantData.phone || "",
        participantEmail: participantData.email || "",
        notificationPreferences: participantData.notificationPreferences || {
          whatsapp: { enabled: true, frequency: "important" },
          email: { enabled: false },
          push: { enabled: true, frequency: "critical" },
        },
        metadata: {
          joinMethod: "web" as const,
          customFields: participantData.customFields || {},
          optins: participantData.optins || {},
        },
      };

      console.log('[queueApiService.joinQueue] Sending request body:', requestBody);
      console.log('[queueApiService.joinQueue] Custom fields being sent:', requestBody.metadata.customFields);

      const response = await this.api.post<QueueEntry>(
        `/queues/${queueId}/entries/join`,
        requestBody
      );

      console.log('[queueApiService.joinQueue] Response received:', response.data);
      console.log('[queueApiService.joinQueue] Custom fields in response:', response.data.customFields);

      return response.data;
    } catch (error) {
      console.error("Error joining queue:", error);
      throw error;
    }
  }

  async getPosition(queueId: string): Promise<QueueEntry | null> {
    if (!queueId || queueId === "null" || queueId === "undefined") {
      console.warn("Invalid queueId provided to getPosition:", queueId);
      return null;
    }
    try {
      const response = await this.api.get<QueueEntry>(
        `/queues/${queueId}/entries/position`
      );
      return response.data;
    } catch (error) {
      console.error("Error getting position:", error);
      return null;
    }
  }

  async getPositionPublic(
    queueId: string,
    tempUserId: string
  ): Promise<QueueEntry | null> {
    if (!queueId || queueId === "null" || queueId === "undefined") {
      console.warn("Invalid queueId provided to getPositionPublic:", queueId);
      return null;
    }
    try {
      const response = await this.api.get<QueueEntry>(
        `/queues/${queueId}/entries/position/public`,
        {
          params: { tempUserId },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error getting public position:", error);
      return null;
    }
  }

  async getEntryById(entryId: string): Promise<QueueEntry | null> {
    try {
      const response = await this.api.get<QueueEntry>(
        `/entries/${entryId}/public`
      );
      return response.data;
    } catch (error) {
      console.error("Error getting entry by ID:", error);
      return null;
    }
  }

  async leaveQueue(queueId: string): Promise<void> {
    try {
      await this.api.post(`/queues/${queueId}/entries/leave`);
    } catch (error) {
      console.error("Error leaving queue:", error);
      throw error;
    }
  }

  async leaveQueuePublic(queueId: string, tempUserId: string): Promise<void> {
    try {
      await this.api.post(`/queues/${queueId}/entries/leave/public`, {
        tempUserId,
      });
    } catch (error: any) {
      console.error(
        "Error leaving queue (public):",
        error.response?.data || error
      );
      throw error;
    }
  }

  // Analytics
  async getQueueMetrics(queueId?: string): Promise<AgencyMetrics> {
    try {
      const response = await this.api.get<{
        totalEntries: number;
        activeEntries: number;
        completedEntries: number;
        abandonedEntries: number;
      }>("/analytics/metrics", {
        params: { queueId },
      });

      return {
        totalInscritos: response.data.totalEntries,
        totalParticipantes: response.data.completedEntries,
        totalDesistentes: response.data.abandonedEntries,
      };
    } catch (error) {
      console.error("Error getting queue metrics:", error);
      throw error;
    }
  }

  async getDashboardData(dateRange?: {
    start: string;
    end: string;
  }): Promise<DashboardData> {
    try {
      const params: Record<string, string> = {};
      if (dateRange?.start) params.startDate = dateRange.start;
      if (dateRange?.end) params.endDate = dateRange.end;

      const response = await this.api.get<DashboardData>(
        "/analytics/dashboard",
        { params }
      );
      return response.data;
    } catch (error) {
      console.error("Error getting dashboard data:", error);
      throw error;
    }
  }

  async getMecanicasInscritos(): Promise<MecanicaInscritos[]> {
    try {
      const dashboard = await this.getDashboardData();
      return dashboard.queueDistribution.map((item) => ({
        mecanicaId: item.queueId,
        mecanicaNome: item.queueName,
        totalInscritos: item.count,
      }));
    } catch (error) {
      console.error("Error getting mecanicas inscritos:", error);
      throw error;
    }
  }

  async getParticipacaoPorHora(): Promise<ParticipacaoPorHora[]> {
    try {
      const dashboard = await this.getDashboardData();
      return dashboard.hourlyParticipation.map((item) => ({
        hora: item.hour,
        participantes: item.count,
      }));
    } catch (error) {
      console.error("Error getting participacao por hora:", error);
      throw error;
    }
  }

  async getFaixasIdade(): Promise<FaixaIdade[]> {
    // Age group data is not available from the dashboard endpoint
    return [
      { faixa: "18-25 anos", quantidade: 0, percentual: 0 },
      { faixa: "26-35 anos", quantidade: 0, percentual: 0 },
      { faixa: "36-45 anos", quantidade: 0, percentual: 0 },
      { faixa: "46-55 anos", quantidade: 0, percentual: 0 },
      { faixa: "56+ anos", quantidade: 0, percentual: 0 },
    ];
  }

  // Configuration
  async getConfiguracaoSolucao(eventId?: string): Promise<ConfiguracaoSolucao> {
    try {
      // If eventId is provided, use public endpoint (for unauthenticated users)
      if (eventId) {
        const response = await this.api.get<ConfiguracaoSolucao>(
          `/configuration/public/${eventId}`
        );
        return response.data;
      }

      // Otherwise use authenticated endpoint
      const response = await this.api.get<ConfiguracaoSolucao>(
        "/configuration"
      );
      return response.data;
    } catch (error) {
      console.error("Error getting configuration:", error);

      // If it's a 404 or 401, return defaults
      if (error instanceof AxiosError) {
        const status = error.response?.status;
        if (status === 404 || status === 401) {
          console.warn(
            "Configuration not found or unauthorized, using defaults"
          );
          return {
            id: "config1",
            corPrincipal: "#4369F5",
            corTexto: "#FFFFFF",
            logo: "/logo.png",
            imagemFundo: "/idVIsual.webp",
            termosUso: "",
            modoAgendamento: false,
          };
        }
      }

      // For other errors, throw to let the caller handle it
      throw error;
    }
  }

  async saveConfiguracaoSolucao(
    config: Partial<ConfiguracaoSolucao>
  ): Promise<ConfiguracaoSolucao> {
    try {
      const response = await this.api.put<ConfiguracaoSolucao>(
        "/configuration",
        config
      );
      return response.data;
    } catch (error) {
      console.error("Error saving configuration:", error);
      throw error;
    }
  }

  async uploadConfigurationImage(
    file: File,
    type: "logo" | "background"
  ): Promise<{ url: string; key: string }> {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", type);

      const response = await this.api.post<{ url: string; key: string }>(
        "/configuration/upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error uploading configuration image:", error);
      throw error;
    }
  }

  // Reports
  async getRelatorioParticipantes(filters?: {
    queueId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{
    participants: RelatorioParticipante[];
    customFieldsConfig: any[];
    total: number;
  }> {
    try {
      const params: Record<string, string> = {};
      if (filters?.queueId) params.queueId = filters.queueId;
      if (filters?.startDate) params.startDate = filters.startDate;
      if (filters?.endDate) params.endDate = filters.endDate;

      const response = await this.api.get<{
        participants: Array<{
          _id: string;
          userName: string;
          userPhone: string;
          userEmail: string;
          userDocument: string;
          queueName: string;
          status: string;
          position: number;
          joinedAt: string;
          customFields: Record<string, any>;
          optins: Record<string, boolean>;
        }>;
        customFieldsConfig: any[];
        total: number;
      }>("/reports/participants", { params });

      const participants = response.data.participants.map((item) => ({
        id: item._id,
        nome: item.userName,
        telefone: item.userPhone,
        email: item.userEmail,
        cpf: item.userDocument,
        mecanica: item.queueName,
        status: item.status,
        posicao: item.position,
        cadastro: item.joinedAt,
        customFields: item.customFields || {},
        optins: item.optins || {},
      }));

      return {
        participants,
        customFieldsConfig: response.data.customFieldsConfig || [],
        total: response.data.total,
      };
    } catch (error) {
      console.error("Error getting participant report:", error);
      throw error;
    }
  }

  // Access Management
  async getUsuariosAcesso(): Promise<UsuarioAcesso[]> {
    try {
      const response = await this.api.get<UsuarioAcesso[]>("/users");
      return response.data;
    } catch (error) {
      console.error("Error getting users:", error);
      throw error;
    }
  }

  async saveUsuarioAcesso(
    usuario: Partial<UsuarioAcesso>
  ): Promise<UsuarioAcesso> {
    try {
      if (usuario.id) {
        const response = await this.api.put<UsuarioAcesso>(
          `/users/${usuario.id}`,
          usuario
        );
        return response.data;
      } else {
        const response = await this.api.post<UsuarioAcesso>("/users", usuario);
        return response.data;
      }
    } catch (error) {
      console.error("Error saving user:", error);
      throw error;
    }
  }

  // Export functionality
  async exportParticipants(
    queueId: string,
    format: "csv" | "excel" = "csv"
  ): Promise<Blob> {
    try {
      const response = await this.api.get(`/exports/participants`, {
        params: { queueId, format },
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      console.error("Error exporting participants:", error);
      throw error;
    }
  }

  // Custom fields management (Queue-specific - legacy)
  async getQueueCustomFields(queueId: string): Promise<CustomFieldConfig[]> {
    try {
      const response = await this.api.get<CustomFieldConfig[]>(`/queues/${queueId}/custom-fields`);
      return response.data;
    } catch (error) {
      console.error("Error getting custom fields:", error);
      return [];
    }
  }

  async updateQueueCustomFields(queueId: string, fields: CustomFieldConfig[]): Promise<void> {
    try {
      await this.api.put(`/queues/${queueId}/custom-fields`, { fields });
    } catch (error) {
      console.error("Error updating custom fields:", error);
      throw error;
    }
  }

  // Custom fields management (Event-level - new)
  async getEventCustomFields(eventId: string): Promise<CustomFieldConfig[]> {
    try {
      const response = await this.api.get<CustomFieldConfig[]>(`/events/${eventId}/custom-fields`);
      return response.data;
    } catch (error) {
      console.error("Error getting event custom fields:", error);
      return [];
    }
  }

  async updateEventCustomFields(eventId: string, fields: CustomFieldConfig[]): Promise<void> {
    try {
      await this.api.put(`/events/${eventId}/custom-fields`, { customFieldsConfig: fields });
    } catch (error) {
      console.error("Error updating event custom fields:", error);
      throw error;
    }
  }

  // Opt-ins management (Event-level)
  async getEventOptins(eventId: string): Promise<OptinConfig[]> {
    try {
      const response = await this.api.get<OptinConfig[]>(`/events/${eventId}/optins`);
      return response.data;
    } catch (error) {
      console.error("Error getting event optins:", error);
      return [];
    }
  }

  async updateEventOptins(eventId: string, optins: OptinConfig[]): Promise<void> {
    try {
      await this.api.put(`/events/${eventId}/optins`, { optinsConfig: optins });
    } catch (error) {
      console.error("Error updating event optins:", error);
      throw error;
    }
  }

  async uploadOptinDocument(eventId: string, file: File): Promise<{ url: string; key: string; originalName: string }> {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await this.api.post<{ url: string; key: string; originalName: string }>(
        `/events/${eventId}/optins/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error uploading optin document:", error);
      throw error;
    }
  }
}

export const queueApiService = new QueueApiService();

export function previewEmailTemplate(
  html: string,
  sampleData: Record<string, string>,
): string {
  let result = html;
  for (const [key, value] of Object.entries(sampleData)) {
    result = result.split(key).join(value);
  }
  // Remove <script> tags and on* event handlers
  result = result.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "");
  result = result.replace(/\s+on\w+\s*=\s*"[^"]*"/gi, "");
  result = result.replace(/\s+on\w+\s*=\s*'[^']*'/gi, "");
  return result;
}
